import { Container, FederatedPointerEvent, Graphics, Rectangle } from 'pixi.js';

const BAR_W = 182;
const BAR_H = 38;
const BTN = 32;
const PAD = 6;
const GAP = 8;
const ICON_C = BTN / 2; // 16

interface Callbacks {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  onClose: () => void;
}

type IconKind = 'plus' | 'minus' | 'reset' | 'close';

export class ZoomControlBar extends Container {
  readonly barWidth = BAR_W;

  private zoomInBtn!: Container;
  private zoomOutBtn!: Container;
  private readonly callbacks: Callbacks;

  constructor(callbacks: Callbacks) {
    super();
    this.callbacks = callbacks;
    this.eventMode = 'static';
    this.hitArea = new Rectangle(0, 0, BAR_W, BAR_H);
    this.bindEventGuards();

    this.drawBackground();
    this.buildChildren();
  }

  /** 统一吞掉控制条上的指针事件，避免冒泡到 stage 触发拖拽或双击缩放。 */
  private bindEventGuards() {
    this.on('pointerdown', this.stopInteractionEvent);
    this.on('pointerup', this.stopInteractionEvent);
    this.on('pointerupoutside', this.stopInteractionEvent);
    this.on('pointertap', this.stopInteractionEvent);
    this.on('globalpointermove', this.stopInteractionEvent);
  }

  /** 阻止控制条事件继续传递给书本交互层。 */
  private readonly stopInteractionEvent = (e: FederatedPointerEvent) => {
    e.stopPropagation();
  };

  private drawBackground() {
    const bg = new Graphics();
    bg.roundRect(0, 0, BAR_W, BAR_H, 4)
      .fill({ color: 0x2c2c2c, alpha: 0.5 })
      .stroke({ width: 1, color: 0xffffff, alpha: 0.16, alignment: 1 });
    this.addChild(bg);
  }

  private buildChildren() {
    let x = PAD;
    const y = (BAR_H - BTN) / 2;

    this.zoomInBtn = this.makeButton('plus', this.callbacks.onZoomIn);
    this.zoomInBtn.position.set(x, y);
    this.addChild(this.zoomInBtn);
    x += BTN + GAP;

    this.zoomOutBtn = this.makeButton('minus', this.callbacks.onZoomOut);
    this.zoomOutBtn.position.set(x, y);
    this.addChild(this.zoomOutBtn);
    x += BTN + GAP;

    this.addChild(this.makeDivider(x, y));
    x += 1 + GAP;

    const reset = this.makeButton('reset', this.callbacks.onReset);
    reset.position.set(x, y);
    this.addChild(reset);
    x += BTN + GAP;

    this.addChild(this.makeDivider(x, y));
    x += 1 + GAP;

    const close = this.makeButton('close', this.callbacks.onClose);
    close.position.set(x, y);
    this.addChild(close);
  }

  private makeDivider(x: number, y: number): Graphics {
    const d = new Graphics();
    d.rect(0, 0, 1, BTN).fill({ color: 0xffffff, alpha: 0.1 });
    d.position.set(x, y);
    return d;
  }

  private makeButton(kind: IconKind, onTap: () => void): Container {
    const btn = new Container();
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    btn.hitArea = new Rectangle(0, 0, BTN, BTN);

    const hover = new Graphics();
    hover.roundRect(0, 0, BTN, BTN, 6).fill({ color: 0xffffff, alpha: 0.1 });
    hover.visible = false;
    btn.addChild(hover);

    btn.addChild(this.makeIcon(kind));

    btn.on('pointerover', () => (hover.visible = true));
    btn.on('pointerout', () => (hover.visible = false));
    btn.on('pointertap', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      onTap();
    });
    btn.on('pointerdown', (e: FederatedPointerEvent) => e.stopPropagation());
    btn.on('pointerup', (e: FederatedPointerEvent) => e.stopPropagation());

    return btn;
  }

  private makeIcon(kind: IconKind): Graphics {
    const g = new Graphics();
    const w = 1.5;
    const c = 0xffffff;
    if (kind === 'plus') {
      g.moveTo(ICON_C - 7, ICON_C).lineTo(ICON_C + 7, ICON_C);
      g.moveTo(ICON_C, ICON_C - 7).lineTo(ICON_C, ICON_C + 7);
      g.stroke({ width: w, color: c });
    } else if (kind === 'minus') {
      g.moveTo(ICON_C - 7, ICON_C)
        .lineTo(ICON_C + 7, ICON_C)
        .stroke({ width: w, color: c });
    } else if (kind === 'close') {
      g.moveTo(ICON_C - 6, ICON_C - 6).lineTo(ICON_C + 6, ICON_C + 6);
      g.moveTo(ICON_C + 6, ICON_C - 6).lineTo(ICON_C - 6, ICON_C + 6);
      g.stroke({ width: w, color: c });
    } else {
      // reset: 放大镜 + 镜内减号
      g.circle(ICON_C - 2, ICON_C - 2, 5).stroke({ width: w, color: c });
      g.moveTo(ICON_C + 1.5, ICON_C + 1.5)
        .lineTo(ICON_C + 6, ICON_C + 6)
        .stroke({ width: w, color: c });
      g.moveTo(ICON_C - 5, ICON_C - 2)
        .lineTo(ICON_C + 1, ICON_C - 2)
        .stroke({ width: 1.2, color: c });
    }
    return g;
  }

  /** 根据当前 zoom 置灰已到边界的 +/− 按钮。 */
  setZoom(zoom: number, min: number, max: number) {
    this.applyEnabled(this.zoomInBtn, zoom < max - 1e-6);
    this.applyEnabled(this.zoomOutBtn, zoom > min + 1e-6);
  }

  private applyEnabled(btn: Container, enabled: boolean) {
    btn.alpha = enabled ? 1 : 0.3;
    btn.eventMode = enabled ? 'static' : 'none';
    btn.cursor = enabled ? 'pointer' : 'default';
  }

  show() {
    this.visible = true;
  }

  hide() {
    this.visible = false;
  }
}
